import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box, Button, Checkbox, HStack, Input, Link, Menu, VStack, Text, Table, Spinner, IconButton, Select, Portal, createListCollection,
} from "@chakra-ui/react";
import { HiOutlineCpuChip, HiArrowUp, HiArrowDown, HiStar, HiEllipsisVertical, HiPlus } from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { CampaignService } from "@/services/CampaignService";
import { LeadService } from "@/services/LeadService";
import { fromApiResponse } from "@/services/campaignMapper";
import CampaignFormModal from "@/components/campaign/CampaignFormModal";
import { CampaignStatusBadge } from "./components/CampaignStatusBadge";
import LeadStatusBadge from "./components/LeadStatusBadge";
import LeadInterestLevelBadge from "./components/LeadInterestLevelBadge";
import TestBadge from "./components/TestBadge";
import InfoField from "./components/InfoField";
import DateInput from "./components/DateInput";
import useLeadSingleActions from "./hooks/useLeadSingleActions";
import dayjs from "dayjs";
import { FormSwitch } from "@/components/jobOffer/ui";
import { toaster } from "@/components/ui/toaster";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";

const INTEREST_LEVEL_OPTIONS = [
    { label: "Niskie", value: "Low" },
    { label: "Średnie", value: "Medium" },
    { label: "Wysokie", value: "High" },
];

const CONVERTED_OPTIONS = [
    { label: "Wszyscy", value: "all" },
    { label: "Skonwertowani", value: "true" },
    { label: "Niekonwertowani", value: "false" },
];

const LEAD_STATUS_OPTIONS = [
    { label: "Nowy", value: "New" },
    { label: "Próba kontaktu", value: "ContactAttempted" },
    { label: "Skontaktowano", value: "Contacted" },
    { label: "Rozmowa", value: "Conversation" },
    { label: "Kwalifikacja", value: "Qualifying" },
    { label: "Zakwalifikowany", value: "Qualified" },
    { label: "Zainteresowany", value: "Interested" },
    { label: "Zaproponowano konwersję", value: "ConversionProposed" },
    { label: "Skonwertowany", value: "Converted" },
    { label: "Niezainteresowany", value: "NotInterested" },
    { label: "Brak odpowiedzi", value: "NoResponse" },
    { label: "Zdyskwalifikowany", value: "Disqualified" },
    { label: "Przekazano do człowieka", value: "HandedToHuman" },
];

const LeadsPage = () => {
    const { id, campaignId } = useParams();
    const navigate = useNavigate();

    const [campaign, setCampaign] = useState(null);
    const [leads, setLeads] = useState([]);
    const [isCampaignLoading, setIsCampaignLoading] = useState(true);
    const [isLeadsLoading, setIsLeadsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState([]);
    const [sourceFilter, setSourceFilter] = useState([]);
    const [availableSources, setAvailableSources] = useState([]);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [convertedFilter, setConvertedFilter] = useState("all");
    const [interestLevelFilter, setInterestLevelFilter] = useState([]);
    const [sortBy, setSortBy] = useState("createdat");
    const [sortDir, setSortDir] = useState("desc");
    const [showFavoriteOnly, setShowFavoriteOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [leadFavLoadingId, setLeadFavLoadingId] = useState(null);
    const [leadsRefreshKey, setLeadsRefreshKey] = useState(0);
    const [showTestLeads, setShowTestLeads] = useState(false);
    const [isCreatingTestLead, setIsCreatingTestLead] = useState(false);
    const [isImportingLeads, setIsImportingLeads] = useState(false);
    const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
    const [isRunningOutput, setIsRunningOutput] = useState(false);
    const [formModal, setFormModal] = useState(null);

    const { openStatusModal, openInterestLevelModal, openNoteModal, modals: singleActionModals } = useLeadSingleActions({
        onStatusChanged: (leadId, updated) =>
            setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: updated.status } : l)),
        onNoteAdded: () => setLeadsRefreshKey(k => k + 1),
        onTimelineEntryAdded: () => setLeadsRefreshKey(k => k + 1),
        onInterestLevelChanged: (leadId, updated) =>
            setLeads(prev => prev.map(l => l.id === leadId ? { ...l, interestLevel: updated.interestLevel } : l)),
    });

    const statusCollection = createListCollection({ items: LEAD_STATUS_OPTIONS });
    const sourceCollection = createListCollection({ items: availableSources.map(s => ({ label: s, value: s })) });
    const interestLevelCollection = createListCollection({ items: INTEREST_LEVEL_OPTIONS });
    const convertedCollection = createListCollection({ items: CONVERTED_OPTIONS });

    useSetPageHeader("Leady", "", <HiOutlineCpuChip />);

    const fetchCampaign = () => CampaignService.getById(campaignId).then(setCampaign);

    useEffect(() => {
        setIsCampaignLoading(true);
        fetchCampaign().finally(() => setIsCampaignLoading(false));
    }, [campaignId]);

    useEffect(() => {
        LeadService.getSources(campaignId).then(setAvailableSources);
    }, [campaignId, leadsRefreshKey]);

    const handleEditCampaign = async () => {
        const data = await CampaignService.getById(campaignId);
        setFormModal({ initialValues: fromApiResponse(data) });
    };

    useEffect(() => {
        const timer = setTimeout(() => { setDebouncedSearch(searchQuery); setPage(1); }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        setSelectedLeadIds(new Set());
        setIsLeadsLoading(true);
        const converted = convertedFilter === "all" ? null : convertedFilter === "true";
        LeadService.getByCampaign(
            campaignId, page, pageSize, statusFilter, showFavoriteOnly || null, sortBy, sortDir,
            debouncedSearch || null, showTestLeads, sourceFilter,
            dateFrom ? new Date(dateFrom).toISOString() : null,
            dateTo ? new Date(dateTo).toISOString() : null,
            converted, interestLevelFilter
        )
            .then((data) => {
                setLeads(data?.items ?? []);
                setTotalCount(data?.totalCount ?? 0);
            })
            .finally(() => setIsLeadsLoading(false));
    }, [
        campaignId, page, pageSize, statusFilter, sortBy, sortDir, showFavoriteOnly, debouncedSearch, showTestLeads,
        sourceFilter, dateFrom, dateTo, convertedFilter, interestLevelFilter, leadsRefreshKey,
    ]);

    const isAllSelected = leads.length > 0 && selectedLeadIds.size === leads.length;
    const isIndeterminate = selectedLeadIds.size > 0 && selectedLeadIds.size < leads.length;

    const toggleSelectLead = (leadId) => setSelectedLeadIds((prev) => {
        const next = new Set(prev);
        next.has(leadId) ? next.delete(leadId) : next.add(leadId);
        return next;
    });

    const toggleSelectAllLeads = () => setSelectedLeadIds(isAllSelected ? new Set() : new Set(leads.map((l) => l.id)));

    const handleRunOutput = async () => {
        setIsRunningOutput(true);
        try {
            const result = await CampaignService.runOutputForLeads(campaignId, Array.from(selectedLeadIds));
            toaster.create({
                type: "success",
                title: `Output uruchomiony dla ${result?.succeeded ?? 0} zapisów`,
                description: `Pominięto: ${result?.skipped ?? 0}, błędy: ${result?.failed ?? 0}`,
            });
            setSelectedLeadIds(new Set());
        } catch (error) {
            toaster.create({ type: "error", title: extractBusinessErrorMessage(error) ?? "Nie udało się uruchomić outputu" });
        } finally {
            setIsRunningOutput(false);
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortBy(field); setSortDir("desc"); }
        setPage(1);
    };

    const handleToggleLeadFavorite = async (e, leadId) => {
        e.stopPropagation();
        setLeadFavLoadingId(leadId);
        const updated = await LeadService.toggleFavorite(leadId);
        setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, isFavourite: updated.isFavourite } : l));
        setLeadFavLoadingId(null);
    };

    const handleCreateTestLead = async () => {
        setIsCreatingTestLead(true);
        try {
            const result = await CampaignService.createTestLead(campaignId);
            toaster.create({ type: "success", title: "Testowy lead utworzony" });
            navigate(`/models/${id}/campaigns/${campaignId}/leads/${result.leadId}`);
        } catch (error) {
            toaster.create({
                type: "error",
                title: extractBusinessErrorMessage(error) ?? "Nie udało się utworzyć testowego leada",
            });
        } finally {
            setIsCreatingTestLead(false);
        }
    };

    const handleImportLeads = async () => {
        setIsImportingLeads(true);
        try {
            const result = await CampaignService.importLeadsFromSource(campaignId);
            toaster.create({
                type: "success",
                title: `Zaimportowano ${result?.imported ?? 0} leadów`,
                description: `Pominięto: ${result?.skippedDuplicates ?? 0} duplikatów, ${result?.excludedByFilter ?? 0} niespełniających warunku`,
            });
            setLeadsRefreshKey(k => k + 1);
        } catch (error) {
            toaster.create({
                type: "error",
                title: extractBusinessErrorMessage(error) ?? "Nie udało się pobrać leadów ze źródła",
            });
        } finally {
            setIsImportingLeads(false);
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <>
        <VStack w="full" py={6} gap={6} align="flex-start">
            {isCampaignLoading ? (
                <VStack w="full" py={8} align="center" justify="center" bg="rgba(0,0,0,0.1)" borderRadius={12} minH={100}>
                    <Spinner color="brand.400" size="lg" />
                </VStack>
            ) : campaign && (
                <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                    <HStack justify="space-between" align="flex-start" mb={4} flexWrap="wrap" gap={2}>
                        <Text fontWeight={700} fontSize="lg" color="white">{campaign.name}</Text>
                        <Button variant="outlined" size="sm" onClick={handleEditCampaign}>Edytuj kampanię</Button>
                    </HStack>
                    <HStack gap={8} align="flex-start" flexWrap="wrap">
                        <InfoField label="Status"><CampaignStatusBadge status={campaign.status} /></InfoField>
                        <InfoField label="Utworzono">
                            <Text color="white" fontSize="sm">{new Date(campaign.createdAtUtc).toLocaleDateString('pl-PL')}</Text>
                        </InfoField>
                        {campaign.agentDisplayName && (
                            <InfoField label="Handlowiec AI"><Text color="white" fontSize="sm">{campaign.agentDisplayName}</Text></InfoField>
                        )}
                    </HStack>
                </Box>
            )}

            <VStack w="full" align="flex-start" gap={3}>
                <HStack w="full" justify="space-between" align="center" flexWrap="wrap" gap={3}>
                    <HStack>
                        <Text fontWeight={700} fontSize="lg" color="white">Lista leadów</Text>
                        <IconButton
                            variant="ghost" size="sm" aria-label="Ulubione"
                            color={showFavoriteOnly ? "yellow.400" : "gray.500"}
                            onClick={() => { setShowFavoriteOnly(v => !v); setPage(1); }}
                        >
                            <HiStar />
                        </IconButton>
                    </HStack>
                    <HStack gap={3} flexWrap="nowrap" align="center">
                        <Box flexShrink={0}>
                            <FormSwitch
                                label="Pokaż testowe leady"
                                checked={showTestLeads}
                                onChange={(v) => { setShowTestLeads(v); setPage(1); }}
                            />
                        </Box>
                        <Button
                            variant="outlined" size="xs" fontSize="xs" loading={isCreatingTestLead}
                            onClick={handleCreateTestLead}
                            flexShrink={0}
                        >
                            <HiPlus /> Utwórz testowego leada
                        </Button>
                        {(campaign?.leadSourceType === "GoogleSheets" || campaign?.leadSourceType === "Excel") && (
                            <Button
                                variant="outlined" size="xs" fontSize="xs" loading={isImportingLeads}
                                onClick={handleImportLeads}
                                flexShrink={0}
                            >
                                Pobierz leady ze źródła
                            </Button>
                        )}
                    </HStack>
                    <HStack gap={2} display={{ base: 'none', md: 'flex' }}>
                        <Input
                            placeholder="Szukaj..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="md" w="200px" bg="bg.card" border="1px solid" borderColor="border.primary"
                            borderRadius="md" color="white" _placeholder={{ color: "text.grey" }}
                        />
                        <Select.Root
                            collection={statusCollection} value={statusFilter}
                            onValueChange={(e) => { setStatusFilter(e.value); setPage(1); }}
                            multiple size="md" w="200px"
                        >
                            <Select.HiddenSelect />
                            <Select.Control>
                                <Select.Trigger h="10" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius="md" fontSize="sm" color="white">
                                    <Select.ValueText color="white" placeholder="Wszystkie statusy" />
                                </Select.Trigger>
                                <Select.IndicatorGroup><Select.Indicator color="white" /></Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                                <Select.Content bg="bg.card" borderColor="border.primary">
                                    {statusCollection.items.map((opt) => (
                                        <Select.Item item={opt} key={opt.value} color="white">
                                            {opt.label}<Select.ItemIndicator />
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Select.Root>
                    </HStack>
                </HStack>

                <HStack gap={2} w="full" flexWrap="wrap" display={{ base: 'none', md: 'flex' }}>
                    <Select.Root
                        collection={sourceCollection} value={sourceFilter}
                        onValueChange={(e) => { setSourceFilter(e.value); setPage(1); }}
                        multiple size="md" w="180px"
                    >
                        <Select.HiddenSelect />
                        <Select.Control>
                            <Select.Trigger h="10" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius="md" fontSize="sm" color="white">
                                <Select.ValueText color="white" placeholder="Wszystkie źródła" />
                            </Select.Trigger>
                            <Select.IndicatorGroup><Select.Indicator color="white" /></Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                            <Select.Content bg="bg.card" borderColor="border.primary">
                                {sourceCollection.items.map((opt) => (
                                    <Select.Item item={opt} key={opt.value} color="white">
                                        {opt.label}<Select.ItemIndicator />
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select.Positioner>
                    </Select.Root>

                    <DateInput
                        placeholder="Data od" value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                        w="150px" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius="md"
                    />
                    <DateInput
                        placeholder="Data do" value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                        w="150px" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius="md"
                    />

                    <Select.Root
                        collection={interestLevelCollection} value={interestLevelFilter}
                        onValueChange={(e) => { setInterestLevelFilter(e.value); setPage(1); }}
                        multiple size="md" w="200px"
                    >
                        <Select.HiddenSelect />
                        <Select.Control>
                            <Select.Trigger h="10" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius="md" fontSize="sm" color="white">
                                <Select.ValueText color="white" placeholder="Wszystkie poziomy zainteresowania" />
                            </Select.Trigger>
                            <Select.IndicatorGroup><Select.Indicator color="white" /></Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                            <Select.Content bg="bg.card" borderColor="border.primary">
                                {interestLevelCollection.items.map((opt) => (
                                    <Select.Item item={opt} key={opt.value} color="white">
                                        {opt.label}<Select.ItemIndicator />
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select.Positioner>
                    </Select.Root>

                    <Select.Root
                        collection={convertedCollection} value={[convertedFilter]}
                        onValueChange={(e) => { setConvertedFilter(e.value[0] ?? "all"); setPage(1); }}
                        size="md" w="170px"
                    >
                        <Select.HiddenSelect />
                        <Select.Control>
                            <Select.Trigger h="10" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius="md" fontSize="sm" color="white">
                                <Select.ValueText color="white" />
                            </Select.Trigger>
                            <Select.IndicatorGroup><Select.Indicator color="white" /></Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                            <Select.Content bg="bg.card" borderColor="border.primary">
                                {convertedCollection.items.map((opt) => (
                                    <Select.Item item={opt} key={opt.value} color="white">
                                        {opt.label}<Select.ItemIndicator />
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select.Positioner>
                    </Select.Root>
                </HStack>

                <Box w="full" position="relative">
                    {isLeadsLoading && (
                        <VStack position="absolute" inset={0} zIndex={1} align="center" justify="center" bg="rgba(0,0,0,0.1)" borderRadius={12} minH={100}>
                            <Spinner color="brand.400" size="lg" />
                        </VStack>
                    )}

                    {/* Desktop table */}
                    <Box display={{ base: 'none', md: 'block' }} w="full" border="1px solid" borderColor="border.primary" borderRadius={12} overflowX="auto" opacity={isLeadsLoading ? 0.4 : 1} transition="opacity 0.15s">
                        <Table.Root minW="1100px">
                            <Table.Header>
                                <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                    <Table.ColumnHeader py={3} px={5} border="none" w="40px">
                                        <Checkbox.Root
                                            checked={isIndeterminate ? "indeterminate" : isAllSelected}
                                            onCheckedChange={toggleSelectAllLeads}
                                            aria-label="Zaznacz wszystkie"
                                        >
                                            <Checkbox.HiddenInput />
                                            <Checkbox.Control>
                                                <Checkbox.Indicator />
                                            </Checkbox.Control>
                                        </Checkbox.Root>
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader py={3} px={2} border="none" w="40px" />
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none">Imię i nazwisko</Table.ColumnHeader>
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none">Firma</Table.ColumnHeader>
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none">Źródło</Table.ColumnHeader>
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none">Status</Table.ColumnHeader>
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none">Zainteresowanie</Table.ColumnHeader>
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none">E-mail</Table.ColumnHeader>
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none">Telefon</Table.ColumnHeader>
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none" cursor="pointer" onClick={() => handleSort("createdat")} _hover={{ color: "white" }}>
                                        <HStack gap={1}><Text>Utworzono</Text>{sortBy === "createdat" && (sortDir === "asc" ? <HiArrowUp size={12} /> : <HiArrowDown size={12} />)}</HStack>
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none" cursor="pointer" onClick={() => handleSort("updatedat")} _hover={{ color: "white" }}>
                                        <HStack gap={1}><Text>Ostatnia aktywność</Text>{sortBy === "updatedat" && (sortDir === "asc" ? <HiArrowUp size={12} /> : <HiArrowDown size={12} />)}</HStack>
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader color="text.grey" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5} border="none" textAlign="right">Akcje</Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {leads.length === 0 && !isLeadsLoading && (
                                    <Table.Row bg="bg.secondary">
                                        <Table.Cell colSpan={12} py={10} border="none" textAlign="center">
                                            <Text color="text.grey" fontSize="sm">Brak leadów</Text>
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                                {leads.map((lead) => (
                                    <Table.Row
                                        key={lead.id} bg="bg.secondary" borderBottom="1px solid" borderColor="border.primary"
                                        _hover={{ bg: "bg.highlight" }} transition="background 0.15s"
                                        onClick={() => navigate(`/models/${id}/campaigns/${campaignId}/leads/${lead.id}`)}
                                        cursor="pointer"
                                    >
                                        <Table.Cell py={4} px={5} border="none" w="40px" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox.Root
                                                checked={selectedLeadIds.has(lead.id)}
                                                onCheckedChange={() => toggleSelectLead(lead.id)}
                                                aria-label="Zaznacz leada"
                                            >
                                                <Checkbox.HiddenInput />
                                                <Checkbox.Control>
                                                    <Checkbox.Indicator />
                                                </Checkbox.Control>
                                            </Checkbox.Root>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={2} border="none" w="40px" onClick={(e) => e.stopPropagation()}>
                                            <IconButton variant="ghost" size="sm" aria-label="Ulubione" color={lead.isFavourite ? "yellow.400" : "gray.500"} loading={leadFavLoadingId === lead.id} onClick={(e) => handleToggleLeadFavorite(e, lead.id)}>
                                                <HiStar />
                                            </IconButton>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <HStack gap={2}>
                                                <Text color="white" fontSize="sm">{[lead.firstname, lead.lastname].filter(Boolean).join(" ") || "-"}</Text>
                                                {lead.isTest && <TestBadge />}
                                            </HStack>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none"><Text color="white" fontSize="sm">{lead.company ?? "-"}</Text></Table.Cell>
                                        <Table.Cell py={4} px={5} border="none"><Text color="white" fontSize="sm">{lead.source ?? "-"}</Text></Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" onClick={(e) => e.stopPropagation()}>
                                            <Box as="button" cursor="pointer" onClick={() => openStatusModal(lead.id, lead.status)} outline="none">
                                                <LeadStatusBadge status={lead.status} />
                                            </Box>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" onClick={(e) => e.stopPropagation()}>
                                            <Box as="button" cursor="pointer" onClick={() => openInterestLevelModal(lead.id, lead.interestLevel)} outline="none">
                                                {lead.interestLevel ? <LeadInterestLevelBadge interestLevel={lead.interestLevel} /> : <Text color="text.grey" fontSize="sm">-</Text>}
                                            </Box>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none"><Text color="white" fontSize="sm">{lead.email ?? "-"}</Text></Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" onClick={(e) => e.stopPropagation()}>
                                            {lead.phoneNumber ? <Link href={`tel:${lead.phoneNumber}`} color="white" fontSize="sm">{lead.phoneNumber}</Link> : <Text color="white" fontSize="sm">-</Text>}
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none"><Text color="white" fontSize="sm">{dayjs(lead.createdAt).format('DD.MM.YYYY HH:mm')}</Text></Table.Cell>
                                        <Table.Cell py={4} px={5} border="none"><Text color="white" fontSize="sm">{dayjs(lead.updatedAt).format('DD.MM.YYYY HH:mm')}</Text></Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" textAlign="right" onClick={(e) => e.stopPropagation()}>
                                            <Menu.Root>
                                                <Menu.Trigger asChild>
                                                    <IconButton variant="outlined" size="sm" aria-label="Akcje"><HiEllipsisVertical /></IconButton>
                                                </Menu.Trigger>
                                                <Portal>
                                                    <Menu.Positioner>
                                                        <Menu.Content bg="bg.card" border="1px solid" borderColor="border.primary">
                                                            <Menu.Item value="status" onClick={() => openStatusModal(lead.id, lead.status)}>Zmień status</Menu.Item>
                                                            <Menu.Item value="interestLevel" onClick={() => openInterestLevelModal(lead.id, lead.interestLevel)}>Zmień poziom zainteresowania</Menu.Item>
                                                            <Menu.Item value="note" onClick={() => openNoteModal(lead.id)}>Dodaj notatkę</Menu.Item>
                                                        </Menu.Content>
                                                    </Menu.Positioner>
                                                </Portal>
                                            </Menu.Root>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </Box>

                    {/* Mobile card list */}
                    <VStack display={{ base: 'flex', md: 'none' }} w="full" gap={2} opacity={isLeadsLoading ? 0.4 : 1} transition="opacity 0.15s">
                        {leads.length === 0 && !isLeadsLoading && (
                            <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={10} p={6} textAlign="center">
                                <Text color="text.grey" fontSize="sm">Brak leadów</Text>
                            </Box>
                        )}
                        {leads.map((lead) => (
                            <Box
                                key={lead.id} w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={10} p={3}
                                cursor="pointer" onClick={() => navigate(`/models/${id}/campaigns/${campaignId}/leads/${lead.id}`)} _active={{ opacity: 0.8 }}
                            >
                                <HStack justify="space-between" align="flex-start">
                                    <HStack gap={2} flex={1} minW={0}>
                                        <IconButton variant="ghost" size="sm" aria-label="Ulubione" color={lead.isFavourite ? "yellow.400" : "gray.500"} loading={leadFavLoadingId === lead.id} flexShrink={0} onClick={(e) => handleToggleLeadFavorite(e, lead.id)}>
                                            <HiStar />
                                        </IconButton>
                                        <VStack align="flex-start" gap={1} flex={1} minW={0}>
                                            <HStack gap={2}>
                                                <Text color="white" fontSize="sm" fontWeight={600}>{[lead.firstname, lead.lastname].filter(Boolean).join(" ") || "-"}</Text>
                                                {lead.isTest && <TestBadge />}
                                            </HStack>
                                            {lead.company && <Text color="text.grey" fontSize="xs">{lead.company}</Text>}
                                            {lead.email && <Text color="text.grey" fontSize="xs">{lead.email}</Text>}
                                            {lead.source && <Text color="text.grey" fontSize="xs">Źródło: {lead.source}</Text>}
                                            <Text color="text.grey" fontSize="xs" whiteSpace="nowrap">Utworzono: {dayjs(lead.createdAt).format('DD.MM.YYYY HH:mm')}</Text>
                                            <Text color="text.grey" fontSize="xs" whiteSpace="nowrap">Ostatnia aktywność: {dayjs(lead.updatedAt).format('DD.MM.YYYY HH:mm')}</Text>
                                        </VStack>
                                    </HStack>
                                    <VStack gap={1} align="flex-end" flexShrink={0} onClick={(e) => e.stopPropagation()}>
                                        <Box as="button" cursor="pointer" onClick={() => openStatusModal(lead.id, lead.status)} outline="none">
                                            <LeadStatusBadge status={lead.status} />
                                        </Box>
                                        <Box as="button" cursor="pointer" onClick={() => openInterestLevelModal(lead.id, lead.interestLevel)} outline="none">
                                            {lead.interestLevel ? <LeadInterestLevelBadge interestLevel={lead.interestLevel} /> : <Text color="text.grey" fontSize="xs">Zainteresowanie: -</Text>}
                                        </Box>
                                    </VStack>
                                </HStack>
                            </Box>
                        ))}
                    </VStack>
                </Box>

                {totalCount > 0 && (
                    <VStack w="full" gap={2} pt={1}>
                        <HStack justify="space-between" w="full">
                            <HStack gap={2}>
                                <Text color="text.grey" fontSize="sm" display={{ base: 'none', md: 'block' }}>Wierszy na stronę:</Text>
                                <select
                                    value={pageSize}
                                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    style={{
                                        background: "var(--chakra-colors-bg-card)", border: "1px solid var(--chakra-colors-border-primary)",
                                        borderRadius: "6px", color: "white", fontSize: "var(--chakra-fontSizes-sm)", padding: "3px 8px", cursor: "pointer",
                                    }}
                                >
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <Text color="text.grey" fontSize="sm">Strona {page} z {totalPages || 1} ({totalCount})</Text>
                            </HStack>
                            {totalPages > 1 && (
                                <HStack gap={2}>
                                    <Button variant="outlined" size="sm" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>Poprzednia</Button>
                                    <Button variant="outlined" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>Następna</Button>
                                </HStack>
                            )}
                        </HStack>
                    </VStack>
                )}
            </VStack>
        </VStack>
        {selectedLeadIds.size > 0 && (
            <Box
                position="fixed" bottom={4} left={{ base: 4, md: "276px" }} right={4} zIndex={50}
                bg="bg.card" border="1px solid" borderColor="brand.400" borderRadius={10} px={4} py={3}
            >
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                    <HStack gap={3}>
                        <Text color="white" fontSize="sm" fontWeight={600}>Zaznaczono {selectedLeadIds.size}</Text>
                        <Button variant="ghost" size="xs" color="text.grey" onClick={() => setSelectedLeadIds(new Set())}>
                            Odznacz wszystkie
                        </Button>
                    </HStack>
                    <Button variant="outlined" size="sm" loading={isRunningOutput} onClick={handleRunOutput}>
                        Uruchom output
                    </Button>
                </HStack>
            </Box>
        )}
        {singleActionModals}
        {formModal && (
            <CampaignFormModal
                agentName={campaign?.agentDisplayName}
                scenarioId={campaign?.scenario?.id}
                initialValues={formModal.initialValues}
                onClose={() => setFormModal(null)}
                onSubmit={(values) =>
                    CampaignService.update(campaignId, values).then(() => {
                        toaster.create({ type: "success", title: "Kampania zapisana" });
                        fetchCampaign();
                        setFormModal(null);
                    })
                }
            />
        )}
        </>
    );
};

export default LeadsPage;
